import React, { useState, useEffect, useRef } from 'react';
import Header from "./Header";  // ✅ Import your Header component
import Footer from "./Footer";  // ✅ Import your Footer component

// --- HELPER HOOK ---
const useOnScreen = (options) => {
  const ref = useRef();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const currentRef = ref.current;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
        if (currentRef) observer.unobserve(currentRef);
      }
    }, options);

    if (currentRef) observer.observe(currentRef);

    return () => {
      if (currentRef) observer.unobserve(currentRef);
    };
  }, [ref, options]);

  return [ref, isVisible];
};

// --- ANIMATED SECTION ---
const AnimatedSection = ({ children, className, id }) => {
  const [ref, isVisible] = useOnScreen({ threshold: 0.1 });
  return (
    <section
      ref={ref}
      id={id}
      className={`${className} transition-all duration-700 ease-out ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
      }`}
    >
      {children}
    </section>
  );
};

// --- PAGE HEADER ---
const PageHeader = ({ title, subtitle }) => (
  <section className="relative py-24 bg-slate-50">
    <div className="absolute inset-0 bg-gradient-to-b from-blue-50 to-slate-50 pointer-events-none"></div>
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
      <h1 className="text-4xl sm:text-5xl font-extrabold text-slate-900 tracking-tight">{title}</h1>
      <p className="mt-4 max-w-2xl mx-auto text-lg text-slate-600">{subtitle}</p>
    </div>
  </section>
);

// --- MOCK DATA ---
const blogPosts = [
  {
    id: 1,
    category: "AI in Sales",
    title: "How AI is Revolutionizing Cold Outreach in 2025",
    excerpt:
      "Discover the top 5 ways artificial intelligence is transforming sales development, from hyper-personalization to predictive lead scoring.",
    authorName: "Alex Johnson",
    authorAvatar: "https://placehold.co/100x100/93c5fd/1e3a8a?text=AJ",
    date: "September 12, 2025",
    imageUrl: "https://placehold.co/800x600/60a5fa/ffffff?text=AI+Revolution",
  },
  {
    id: 2,
    category: "Productivity",
    title: "10 Productivity Hacks for High-Performing Sales Teams",
    excerpt:
      "Boost your team's efficiency and close more deals. We share our top tips for automating tasks and focusing on what matters.",
    authorName: "Dana White",
    authorAvatar: "https://placehold.co/100x100/fde047/78350f?text=DW",
    date: "September 5, 2025",
    imageUrl: "https://placehold.co/800x600/818cf8/ffffff?text=Productivity",
  },
  {
    id: 3,
    category: "Outreach Tips",
    title: "Getting Started with Outreach: A Step-by-Step Guide",
    excerpt:
      "New to Outreach? This guide will walk you through setting up your first campaign and getting the most out of our powerful features.",
    authorName: "Chris Lee",
    authorAvatar: "https://placehold.co/100x100/f0abfc/4a044e?text=CL",
    date: "August 28, 2025",
    imageUrl: "https://placehold.co/800x600/a78bfa/ffffff?text=Outreach+Guide",
  },
  {
    id: 4,
    category: "Industry Trends",
    title: "The Future of B2B Sales: Predictions for 2026",
    excerpt:
      "What's next for B2B sales? We explore emerging trends, from conversational AI to the rise of community-led growth.",
    authorName: "Brenda Smith",
    authorAvatar: "https://placehold.co/100x100/a7f3d0/052e16?text=BS",
    date: "August 21, 2025",
    imageUrl: "https://placehold.co/800x600/f472b6/ffffff?text=Future+Trends",
  },
];

// --- MAIN PAGE ---
const BlogPage = () => (
  <>
    {/* ✅ Global Header */}
    <Header />

    <main>
      <PageHeader
        title="The Outreach Blog"
        subtitle="Insights, tips, and trends on AI, sales, and the future of outreach."
      />

      <AnimatedSection className="py-24" id="blog-posts">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-3 sm:grid-cols-2">
            {blogPosts.map((post) => (
              <div
                key={post.id}
                className="bg-white rounded-2xl shadow-lg overflow-hidden transition-transform duration-300 hover:-translate-y-2 group"
              >
                <div className="relative">
                  <img src={post.imageUrl} alt={post.title} className="w-full h-56 object-cover" />
                  <div className="absolute top-4 left-4">
                    <span className="bg-blue-600 text-white text-xs font-semibold px-3 py-1 rounded-full">
                      {post.category}
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-slate-900 group-hover:text-blue-600 transition-colors">
                    {post.title}
                  </h3>
                  <p className="mt-3 text-slate-600 text-base">{post.excerpt}</p>
                </div>
                <div className="px-6 pb-6 mt-auto flex items-center gap-4 border-t pt-4">
                  <img src={post.authorAvatar} alt={post.authorName} className="w-10 h-10 rounded-full" />
                  <div>
                    <p className="font-semibold text-slate-800">{post.authorName}</p>
                    <p className="text-sm text-slate-500">{post.date}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-16 text-center">
            <button className="bg-slate-800 text-white font-semibold py-3 px-8 rounded-lg hover:bg-slate-900 transition-colors">
              Load More Posts
            </button>
          </div>
        </div>
      </AnimatedSection>
    </main>

    {/* ✅ Global Footer */}
    <Footer />
  </>
);

export default BlogPage;
